import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Receipt, Download, Eye, ArrowLeft } from "lucide-react";

const cy = (c) => (c === "MYR" ? "RM" : (c || "RM") + " ");

export default function OrganizerBilling() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/organizer/invoices").then(({ data }) => setInvoices(data.invoices)).finally(() => setLoading(false));
  }, []);

  const openPdf = async (inv, download) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (download) {
        const a = document.createElement("a");
        a.href = url; a.download = `${inv.invoice_number}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      } else {
        window.open(url, "_blank");
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch { toast.error("Could not load invoice"); }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/organizer" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft size={15} /> Back to Dashboard</Link>
        <div className="flex items-center gap-3 mb-6">
          <span className="w-11 h-11 rounded-xl bg-[#0a1420] text-[#22d3ee] grid place-items-center"><Receipt size={20} /></span>
          <div>
            <h1 className="font-display font-black uppercase text-3xl text-slate-800">Payments & Invoices</h1>
            <p className="text-sm text-slate-500">Your complete payment history. Invoices stay here permanently.</p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="invoices-table">
          {loading ? <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#22d3ee]" size={28} /></div> :
            invoices.length === 0 ? <p className="p-12 text-center text-slate-500">No invoices yet. Invoices appear here after a successful payment.</p> : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr><th className="text-left p-4">Invoice</th><th className="text-left p-4">Date</th><th className="text-left p-4">Race</th><th className="text-left p-4">Product</th><th className="text-left p-4">Duration</th><th className="text-right p-4">Amount</th><th className="text-right p-4">Status</th><th className="text-right p-4">Actions</th></tr>
                </thead>
                <tbody>{invoices.map((inv) => (
                  <tr key={inv.id} className="border-t border-slate-100" data-testid={`invoice-row-${inv.id}`}>
                    <td className="p-4 font-mono-data font-semibold text-slate-800">{inv.invoice_number}</td>
                    <td className="p-4 text-slate-600">{(inv.issued_at || "").slice(0, 10)}</td>
                    <td className="p-4 text-slate-600">{inv.race_name}</td>
                    <td className="p-4 text-slate-600">{inv.product_label}</td>
                    <td className="p-4 text-slate-600">{inv.duration_label}</td>
                    <td className="p-4 text-right font-mono-data font-semibold">{cy(inv.currency)}{inv.amount}</td>
                    <td className="p-4 text-right"><span className="text-xs font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">{inv.status}</span></td>
                    <td className="p-4 text-right whitespace-nowrap">
                      <button onClick={() => openPdf(inv, false)} className="inline-flex items-center gap-1 text-xs font-bold text-[#06b6d4] hover:underline px-2" data-testid={`view-invoice-${inv.id}`}><Eye size={14} /> View</button>
                      <button onClick={() => openPdf(inv, true)} className="inline-flex items-center gap-1 text-xs font-bold text-slate-600 hover:underline px-2" data-testid={`download-invoice-${inv.id}`}><Download size={14} /> Download</button>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            )}
        </div>
      </div>
    </Layout>
  );
}
